import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { staffCommunicationsAPI } from "@/lib/staff-communications-api";
import type { StaffCommunicationListParams } from "@/lib/staff-communications-api";
import { queryKeys } from "@/lib/query-keys";
import { useToast } from "@/hooks/use-toast";

export const useStaffCommunicationList = (
  params: StaffCommunicationListParams
) => {
  return useQuery({
    queryKey: queryKeys.staffCommunications.list(params),
    queryFn: () => staffCommunicationsAPI.listCommunications(params),
    enabled: !!params.console_user_id,
    retry: false,
  });
};

export const useStaffCommunicationStats = (consoleUserId: string) => {
  return useQuery({
    queryKey: queryKeys.staffCommunications.stats(consoleUserId),
    queryFn: () => staffCommunicationsAPI.getStats(consoleUserId),
    enabled: !!consoleUserId,
    retry: false,
  });
};

export const useRetryStaffCommunication = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => staffCommunicationsAPI.retryCommunication(id),
    onSuccess: () => {
      toast({
        title: "Communication retried",
        description: "The communication has been retried successfully.",
      });
      // Invalidate all staff communication queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["staff-communications"] });
    },
    onError: (error: any) => {
      toast({
        title: "Retry failed",
        description:
          error?.response?.data?.error || "Failed to retry communication.",
        variant: "destructive",
      });
    },
  });
};
